import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { allProducts } from '../data/products';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;

  // Keep input in sync with URL query on products page.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryFromUrl = params.get('search') || '';
    if (location.pathname === '/products') {
      setSearchQuery(queryFromUrl);
    }
  }, [location.pathname, location.search]);

  // Filter products based on search query
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const filtered = allProducts.filter((product) => {
        const query = searchQuery.toLowerCase();
        return (
          product.name.toLowerCase().includes(query) ||
          product.category.toLowerCase().includes(query) ||
          (product.hsn && product.hsn.includes(query))
        );
      }).slice(0, 5); // Show max 5 suggestions
      
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedIndex(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        (!suggestionsRef.current || !suggestionsRef.current.contains(event.target))
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSuggestions]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSuggestions(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (productName) => {
    navigate(`/products?search=${encodeURIComponent(productName)}`);
    setSearchQuery(productName);
    setShowSuggestions(false);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
    if (location.pathname === '/products') {
      navigate('/products');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      if (showSuggestions && suggestions.length > 0) {
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
      }
    } else if (e.key === 'ArrowUp') {
      if (showSuggestions && suggestions.length > 0) {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      }
    } else if (e.key === 'Enter') {
      if (showSuggestions && selectedIndex >= 0 && suggestions.length > 0) {
        e.preventDefault();
        handleSuggestionClick(suggestions[selectedIndex].name);
      }
      // If no suggestion selected, let form submit handle it
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  return (
    <nav className="bg-[#f6f1e6] sticky top-0 z-50">
      <div className="w-full px-3 sm:px-6 lg:px-8">
        <div className="flex items-center h-20 lg:h-24 gap-3">
          <Link to="/" className="flex items-center space-x-3 flex-shrink-0 min-w-0">
            <img src="/Product Images/LOGO.jpeg" alt="Earthly Spice Exports" className="h-12 w-12 sm:h-14 sm:w-14 rounded-full object-cover border border-[#35533a]/30" />
            <div className="min-w-0 text-[#243f2b]">
              <div className="font-semibold leading-tight text-lg sm:text-xl lg:text-[2rem]" style={{ fontFamily: 'Georgia, serif' }}>
                Earthly Spice
              </div>
              <div className="flex items-center justify-center gap-1 mt-1 -ml-1 sm:-ml-10">
                <span className="hidden sm:block h-px w-8 bg-[#35533a]/60"></span>
                <span className="text-[10px] sm:text-xs font-semibold tracking-[0.28em] uppercase">Exports</span>
                <span className="hidden sm:block h-px w-8 bg-[#35533a]/60"></span>
              </div>
              <div className="hidden lg:block text-[10px] tracking-[0.16em] uppercase text-[#4b5f4f] mt-0.5">
                Nature's finest, delivered to the world
              </div>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-7 ml-auto">
            <Link to="/" className={`text-[12px] font-semibold tracking-wide uppercase ${isActive('/') ? 'text-[#1f3a27] border-b border-[#1f3a27]' : 'text-gray-700 hover:text-[#1f3a27]'}`}>Home</Link>
            <Link to="/about" className={`text-[12px] font-semibold tracking-wide uppercase ${isActive('/about') ? 'text-[#1f3a27] border-b border-[#1f3a27]' : 'text-gray-700 hover:text-[#1f3a27]'}`}>About Us</Link>
            <Link to="/products" className={`text-[12px] font-semibold tracking-wide uppercase ${isActive('/products') ? 'text-[#1f3a27] border-b border-[#1f3a27]' : 'text-gray-700 hover:text-[#1f3a27]'}`}>Our Products</Link>
            <Link to="/quality" className={`text-[12px] font-semibold tracking-wide uppercase ${isActive('/quality') ? 'text-[#1f3a27] border-b border-[#1f3a27]' : 'text-gray-700 hover:text-[#1f3a27]'}`}>Quality</Link>
            <Link to="/contact" className={`text-[12px] font-semibold tracking-wide uppercase ${isActive('/contact') ? 'text-[#1f3a27] border-b border-[#1f3a27]' : 'text-gray-700 hover:text-[#1f3a27]'}`}>Contact Us</Link>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <form onSubmit={handleSearch} className="relative w-56" ref={searchRef}>
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                className="w-full px-4 py-2 pl-9 pr-9 text-xs border border-[#d8d0bf] bg-white/80 rounded-full focus:ring-2 focus:ring-[#35533a]/30 focus:border-[#35533a] outline-none"
              />
              <svg
                className="absolute left-3 top-2.5 h-4 w-4 text-[#6f7d68]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-3 top-2.5 text-[#6f7d68] hover:text-[#1f3a27]"
                  aria-label="Clear search"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}

              {showSuggestions && suggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#d8d0bf] rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {suggestions.map((product, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSuggestionClick(product.name);
                      }}
                      onMouseEnter={() => setSelectedIndex(index)}
                      className={`w-full text-left px-4 py-3 hover:bg-[#f6f1e6] transition-colors ${
                        index === selectedIndex ? 'bg-[#f6f1e6]' : ''
                      } ${index > 0 ? 'border-t border-[#ece5d8]' : ''}`}
                    >
                      <div className="font-medium text-gray-900">{product.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{product.category}</div>
                    </button>
                  ))}
                </div>
              )}
            </form>
            <a
              href="https://wa.me/919902043782"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#1f3a27] text-white px-6 py-3 rounded-full text-[12px] font-semibold tracking-wide uppercase hover:bg-[#17301f] transition-colors"
            >
              Enquire Now
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden ml-auto text-gray-700 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-[#d8d0bf] bg-[#f6f1e6]">
            {/* Mobile Search */}
            <form onSubmit={handleSearch} className="px-3 mb-4 relative" ref={searchRef}>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                  className="w-full px-4 py-2 pl-10 pr-10 text-sm border border-[#d8d0bf] bg-white/80 rounded-full focus:ring-2 focus:ring-[#35533a]/30 focus:border-[#35533a] outline-none"
                />
                <svg
                  className="absolute left-3 top-2.5 h-5 w-5 text-[#6f7d68]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchQuery && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-3 top-2.5 text-[#6f7d68] hover:text-[#1f3a27]"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Autocomplete Suggestions Dropdown - Mobile */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute top-full left-3 right-3 mt-1 bg-white border border-[#d8d0bf] rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {suggestions.map((product, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSuggestionClick(product.name);
                        setIsOpen(false);
                      }}
                      onMouseEnter={() => setSelectedIndex(index)}
                      className={`w-full text-left px-4 py-3 hover:bg-[#f6f1e6] transition-colors ${
                        index === selectedIndex ? 'bg-[#f6f1e6]' : ''
                      } ${index > 0 ? 'border-t border-[#ece5d8]' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{product.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{product.category}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </form>
            
            <a
              href="https://wa.me/919902043782"
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-2 mx-3 text-center bg-[#1f3a27] text-white px-4 py-3 rounded-full text-xs font-semibold tracking-wide uppercase hover:bg-[#17301f] transition-colors"
              onClick={() => setIsOpen(false)}
            >
              Enquire Now
            </a>

            <Link to="/" className="block px-3 py-2 text-[12px] font-semibold tracking-wide uppercase text-gray-700 hover:text-[#1f3a27]" onClick={() => setIsOpen(false)}>
              Home
            </Link>
            <Link to="/about" className="block px-3 py-2 text-[12px] font-semibold tracking-wide uppercase text-gray-700 hover:text-[#1f3a27]" onClick={() => setIsOpen(false)}>
              About Us
            </Link>
            <Link to="/products" className="block px-3 py-2 text-[12px] font-semibold tracking-wide uppercase text-gray-700 hover:text-[#1f3a27]" onClick={() => setIsOpen(false)}>
              Products
            </Link>
            <Link to="/quality" className="block px-3 py-2 text-[12px] font-semibold tracking-wide uppercase text-gray-700 hover:text-[#1f3a27]" onClick={() => setIsOpen(false)}>
              Quality & Packaging
            </Link>
            <Link to="/contact" className="block px-3 py-2 text-[12px] font-semibold tracking-wide uppercase text-gray-700 hover:text-[#1f3a27]" onClick={() => setIsOpen(false)}>
              Contact Us
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
